// SPDX-License-Identifier: GPL-3.0-only
// Pure local draft state. Encryption and authenticated storage are upstream responsibilities.
export type FieldName = 'name' | 'url' | 'account' | 'password' | 'username' | 'phone' | 'email' | 'notes';
export interface DraftRecord {
  name: string; url: string; account: string; password: string;
  username: string; phone: string; email: string; notes: string;
  extraFields: { name: string; value: string; hidden: boolean }[];
  warnings: { code: string; message: string }[];
  unparsed: { line: number; text: string }[];
}
export interface ParserPort {
  parse(text: string, options: { format: string; filename?: string }): { records: DraftRecord[] };
  buildExport(records: DraftRecord[]): { items: unknown[] };
}
export interface NativeResult { success: boolean; ciphers: unknown[] }
export type NativeSave = (json: string, expectedUserId: string) => Promise<NativeResult>;

export function insertionFits(current: string, inserted: string, start: number, end: number, maximum: number): boolean {
  return current.length - Math.max(0, end - start) + inserted.length <= maximum;
}

export class LocalDraft {
  records: DraftRecord[] = [];
  confirmed = false;
  busy = false;
  attempted = false;
  phase: 'empty' | 'review' | 'saving' | 'saved' | 'uncertain' = 'empty';
  message = '';
  private owner: string | null = null;
  private generation = 0;
  private parser: ParserPort;

  constructor(parser: ParserPort) { this.parser = parser; }

  organize(text: string, format: string, filename: string, userId: string | null): void {
    if (this.busy || this.phase === 'uncertain') throw new Error('请先在密码库核对上次保存结果，再清空当前资料。');
    if (!userId) throw new Error('请先在官方密码库登录并解锁。');
    this.clear();
    this.records = this.parser.parse(text, { format, filename }).records;
    this.owner = userId;
    this.phase = this.records.length ? 'review' : 'empty';
    this.message = this.records.length ? '已在本机整理，请核对后加密保存。' : '没有可保存的记录。';
  }

  edited(): void {
    if (this.busy) throw new Error('正在保存，暂时不能修改。');
    this.confirmed = false;
    this.generation++;
  }

  clear(force = false): void {
    if (this.busy && !force) throw new Error('保存请求进行中，请等待结果。');
    this.generation++;
    this.records = [];
    this.confirmed = false;
    this.owner = null;
    this.attempted = false;
    this.phase = 'empty';
    this.message = '';
  }

  async save(userId: string | null, nativeSave: NativeSave): Promise<boolean> {
    if (this.busy || this.attempted || this.phase !== 'review' || !this.confirmed || !this.records.length || !userId || userId !== this.owner) {
      throw new Error('请在同一账户中重新整理并核对资料；不会自动重复保存。');
    }
    const expectedCount = this.records.length;
    const json = JSON.stringify(this.parser.buildExport(this.records));
    if (json.length > 2 * 1024 * 1024) throw new Error('编辑后的资料过大，请分批整理；没有截断内容。');
    const generation = this.generation;
    this.busy = true;
    this.attempted = true;
    this.phase = 'saving';
    this.message = '由官方客户端加密保存中，请勿关闭或切换账户。';
    try {
      const result = await nativeSave(json, userId);
      if (result.success !== true || result.ciphers.length !== expectedCount) throw new Error('Native import result mismatch');
      // Logout, account change or destroy invalidates UI callbacks; an old request
      // may still finish for its explicitly bound account, so never auto-resubmit.
      if (this.generation !== generation) return false;
      this.records = [];
      this.confirmed = false;
      this.owner = null;
      this.phase = 'saved';
      this.message = `已收到官方保存成功结果，共 ${expectedCount} 条。请在密码库确认条目。`;
      return true;
    } catch (_) {
      if (this.generation === generation) {
        this.phase = 'uncertain';
        this.confirmed = false;
        this.message = '保存结果未确认。请先查看密码库，可能已保存部分或全部条目；不要直接重复保存。核对后再清空并重新整理。';
      }
      return false;
    } finally {
      this.busy = false;
    }
  }
}

/** Fail before native encryption if upstream conversion changes reviewed secrets. */
export function assertPreserved(json: string, parsed: NativeResult): void {
  const source = JSON.parse(json);
  if (!parsed.success || !Array.isArray(parsed.ciphers) || parsed.ciphers.length !== source.items.length) throw new Error('Native parser mismatch');
  source.items.forEach((item: any, index: number) => {
    const cipher = parsed.ciphers[index] as any;
    if (!cipher || cipher.type !== 1 || cipher.organizationId != null || cipher.id != null || cipher.key != null ||
        cipher.name !== item.name || (cipher.notes ?? null) !== (item.notes ?? null) ||
        cipher.login?.password !== item.login.password || cipher.login?.username !== item.login.username ||
        JSON.stringify((cipher.fields ?? []).map((field: any) => [field.name, field.value, field.type])) !==
        JSON.stringify(item.fields.map((field: any) => [field.name, field.value, field.type])) ||
        JSON.stringify((cipher.login?.uris ?? []).map((uri: any) => [uri.uri, uri.match ?? null])) !==
        JSON.stringify(item.login.uris.map((uri: any) => [uri.uri, uri.match ?? null]))) {
      throw new Error('Native conversion changed reviewed record');
    }
  });
}
